import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useCollaborationData } from '../hooks/useCollaborationData';

/**
 * 收藏的Collaboration卡片组件 - 简化版
 * 参考 FavoriteWorkCard 的实现，直接从 Supabase 获取数据
 * @param {Object} props - 组件属性
 * @param {Object} props.favorite - 收藏数据
 * @param {boolean} props.isOwnProfile - 是否为当前用户查看自己的档案
 * @param {Function} props.onRemove - 移除收藏回调
 */
const FavoriteCollabCard = ({ favorite, isOwnProfile = false, onRemove }) => {
  const navigate = useNavigate();
  const { getCollaborationById, loading: collaborationsLoading } =
    useCollaborationData();

  // 获取 collaboration 数据
  const collaboration = getCollaborationById(favorite.item_id);

  // 如果 collaboration 不存在且不在加载中，自动移除收藏
  if (!collaboration && !collaborationsLoading) {
    console.warn(
      '[FavoriteCollabCard] Collaboration not found, removing favorite:',
      favorite.item_id
    );
    // 延迟执行以避免渲染过程中的状态更新
    setTimeout(() => {
      onRemove();
    }, 0);
    return null;
  }

  // 处理图片加载错误
  const handleImageError = e => {
    console.error('[FavoriteCollabCard] Image failed to load:', e.target.src);
    // 如果图片加载失败，使用默认占位图片
    e.target.style.display = 'none';
    e.target.nextElementSibling.style.display = 'flex';
  };

  const handleRemoveClick = e => {
    e.stopPropagation();
    onRemove();
  };

  const handleCardClick = () => {
    // 跳转到协作详情页，传递完整的协作数据
    if (collaboration) {
      navigate(`/tagme/collaboration/${favorite.item_id}`, {
        state: { project: collaboration },
      });
    } else {
      // 如果没有协作数据，仍然跳转但可能显示错误
      console.warn(
        '[FavoriteCollabCard] No collaboration data available for navigation'
      );
      navigate(`/tagme/collaboration/${favorite.item_id}`);
    }
  };

  if (collaborationsLoading) {
    return (
      <div className='relative aspect-[4/3] bg-gray-200 rounded-lg shadow-sm overflow-hidden'>
        <div className='w-full h-full flex items-center justify-center'>
          <div className='text-gray-500'>Loading...</div>
        </div>
      </div>
    );
  }

  // 获取图片URL（优先使用 posterPreview，回退到 heroImage）
  const imageUrl =
    collaboration?.posterPreview || collaboration?.heroImage || null;

  return (
    <div
      className='relative aspect-[4/3] bg-tag-purple rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden cursor-pointer'
      onClick={handleCardClick}
    >
      {/* 协作图片 */}
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt={collaboration?.title || 'Collaboration'}
            className='w-full h-full object-cover'
            onError={handleImageError}
            onLoad={() =>
              console.log(
                '[FavoriteCollabCard] Image loaded successfully:',
                imageUrl
              )
            }
          />
          <div
            className='w-full h-full bg-gray-200 flex items-center justify-center'
            style={{ display: 'none' }}
          >
            <div className='text-gray-500 text-sm'>No Image</div>
          </div>
        </>
      ) : (
        <div className='w-full h-full bg-gray-200 flex items-center justify-center'>
          <div className='text-gray-500 text-sm'>No Image</div>
        </div>
      )}

      {/* 紫色识别条 */}
      <div className='absolute bottom-0 left-0 right-0 h-2 bg-tag-purple'></div>

      {/* 移除按钮 - 只有档案所有者才能看到 */}
      {isOwnProfile && (
        <button
          onClick={handleRemoveClick}
          className='absolute top-2 right-2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center transition-colors duration-200'
          title='Remove from favorites'
        >
          <svg
            className='w-4 h-4 text-gray-600'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default FavoriteCollabCard;
